import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as PickingIcon,
} from "@mui/icons-material";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  DynamicRequestFilter,
  DynamicRequestSort,
} from "../../types/dynamicQueryTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import {
  ApproveIcon,
  RejectIcon,
} from "../../components/common/NucleusSymbolsAndIcons";
import { NumericDisplayField } from "../../components/common/CustomFields";
import stringUtils from "../../utils/stringUtils";
import statusUtils from "../../utils/statusUtils";

interface ReceiptData {
  lot: string;
  date: Date;
}

interface POLineData {
  id: string;
  line: number;
  itemNum: string;
  itemName: string;
  quantity: number;
  received: number;
  uom: string;
  poId: string;
  purchOrder: string;
  vendorCode: string;
  vendorName: string;
  price: number;
  orderDate: Date;
  receipts: ReceiptData[];
}

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
  placeholder: string;
}> = ({ finalSet, placeholder }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PagePOLineList: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const [targetItem, setTargetItem] = useState<string>("");
  const [targetVendor, setTargetVendor] = useState<string>("");
  const [targetPO, setTargetPO] = useState<string>("");

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [orderBy, setOrderBy] = useState<string>("purchOrder");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const sorts: DynamicRequestSort[] = useMemo(() => {
    switch (orderBy) {
      case "purchOrder":
        return [
          {
            table: "purchase_orders",
            alt_name: "po",
            field: "number",
            asc: order === "asc",
          },
          {
            table: "purchase_order_line_items",
            alt_name: "po_line",
            field: "line",
            asc: true,
          },
        ];
      case "vendor":
        return [
          {
            table: "vendors",
            alt_name: "vend",
            field: "name",
            asc: order === "asc",
          },
          {
            table: "purchase_orders",
            alt_name: "po",
            field: "number",
            asc: true,
          },
          {
            table: "purchase_order_line_items",
            alt_name: "po_line",
            field: "line",
            asc: true,
          },
        ];
      case "orderDate":
        return [
          {
            table: "purchase_orders",
            alt_name: "po",
            field: "date",
            asc: order === "asc",
          },
          {
            table: "purchase_order_line_items",
            alt_name: "po_line",
            field: "line",
            asc: true,
          },
        ];
      case "unitCost":
        return [
          {
            table: "purchase_order_line_items",
            alt_name: "po_line",
            field: "unit_cost",
            asc: order === "asc",
          },
        ];
      default:
        return [
          {
            table: "purchase_order_line_items",
            alt_name: "po_line",
            field: "line",
            asc: true,
          },
        ];
    }
  }, [order, orderBy]);

  const filters: DynamicRequestFilter[][] = useMemo(() => {
    const filts: DynamicRequestFilter[][] = [];
    if (targetVendor !== "") {
      filts[0] = [];
      filts[1] = [];
      filts[0].push({
        table: "vendors",
        alt_name: "vend",
        field: "name",
        operator: "contains",
        value: targetVendor,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "vendors",
        alt_name: "vend",
        field: "code",
        operator: "contains",
        value: targetVendor,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetItem !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "items",
        alt_name: "item",
        field: "number",
        operator: "contains",
        value: targetItem,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "items",
        alt_name: "item",
        field: "number",
        operator: "contains",
        value: targetItem,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetItem !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "items",
        alt_name: "item",
        field: "number",
        operator: "contains",
        value: targetItem,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "items",
        alt_name: "item",
        field: "number",
        operator: "contains",
        value: targetItem,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetPO !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "purchase_orders",
        alt_name: "po",
        field: "number",
        operator: "contains",
        value: targetPO,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "purchase_orders",
        alt_name: "po",
        field: "number",
        operator: "contains",
        value: targetPO,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    return filts;
  }, [targetItem, targetVendor, targetPO]);

  const {
    data: poLineResponse,
    isLoading: poLineLoading,
    isFetching: poLineFetching,
    error: poLineError,
  } = useTSQDynamicList({
    page: pagination.page,
    page_size: pagination.size,
    primary: "purchase_order_line_items",
    alt_name: "po_line",
    primary_fields: [
      "id",
      "line",
      "quantity_ordered",
      "quantity_received",
      "purchase_order_id",
      "unit_cost",
    ],
    joins: [
      {
        table: "purchase_orders",
        alt_name: "po",
        fields: ["number", "order_date"],
        condition: {
          remote_table: "purchase_order_line_items",
          alt_name: "po_line",
          remote_field: "purchase_order_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "vendors",
        alt_name: "vend",
        fields: ["code", "name"],
        condition: {
          remote_table: "purchase_orders",
          alt_name: "po",
          remote_field: "vendor_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "items",
        alt_name: "item",
        fields: ["number", "name"],
        condition: {
          remote_table: "purchase_order_line_items",
          alt_name: "po_line",
          remote_field: "item_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "uom_units",
        alt_name: "uom",
        fields: ["unit_symbol"],
        condition: {
          remote_table: "purchase_order_line_items",
          alt_name: "po_line",
          remote_field: "uom_unit_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: filters,
    sorts: sorts,
    subqueries: [],
  });

  const [poLines, recCount]: [POLineData[], number] = useMemo(() => {
    if (poLineResponse?.items) {
      const poLines: POLineData[] = poLineResponse.items.map((val) => ({
        id: val["po_line.id"],
        line: val["po_line.line"],
        itemNum: val["item.number"],
        itemName: val["item.name"],
        quantity: parseFloat(val["po_line.quantity_ordered"]),
        received: parseFloat(val["po_line.quantity_received"]),
        uom: val["uom.unit_symbol"],
        poId: val["po_line.purchase_order_id"],
        purchOrder: val["po.number"],
        vendorCode: val["vend.code"],
        vendorName: val["vend.name"],
        price: parseFloat(val["po_line.unit_cost"]),
        orderDate: new Date(val["po.order_date"]),
        receipts: [],
      }));
      return [poLines, poLineResponse.total];
    }
    return [[], 0];
  }, [poLineResponse]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Purchase Order Lines
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/orders/purchase/form?mode=create")}
        >
          Create New PO
        </Button>
      </Stack>

      {(error || !!poLineError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || poLineError?.message || "An unknown error occurred"}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" flexWrap={"wrap"} gap={1}>
          <FilterBox
            finalSet={setTargetItem}
            placeholder="Filter by Item Number"
          />
          <FilterBox finalSet={setTargetPO} placeholder="Filter By PO Number" />
          <FilterBox
            finalSet={setTargetVendor}
            placeholder="Filter By Vendor Name/Code"
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={recCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={orderBy === "purchOrder" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "purchOrder"}
                  direction={orderBy === "purchOrder" ? order : "asc"}
                  onClick={() => handleSort("purchOrder")}
                >
                  PO Number
                </TableSortLabel>
              </TableCell>
              <TableCell>Item</TableCell>
              <TableCell sortDirection={orderBy === "vendor" ? order : false}>
                <TableSortLabel
                  active={orderBy === "vendor"}
                  direction={orderBy === "vendor" ? order : "asc"}
                  onClick={() => handleSort("vendor")}
                >
                  Vendor
                </TableSortLabel>
              </TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell sortDirection={orderBy === "unitCost" ? order : false}>
                <TableSortLabel
                  active={orderBy === "unitCost"}
                  direction={orderBy === "unitCost" ? order : "asc"}
                  onClick={() => handleSort("unitCost")}
                >
                  Pricing
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "orderDate" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "orderDate"}
                  direction={orderBy === "orderDate" ? order : "asc"}
                  onClick={() => handleSort("orderDate")}
                >
                  Order Date
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {poLineFetching || poLineLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                  <Typography variant="body1">Loading Data...</Typography>
                </TableCell>
              </TableRow>
            ) : poLines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No PO Lines Found with provided Parameters
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              poLines.map((line) => (
                <TableRow key={line.id} hover>
                  <TableCell>
                    <Typography variant="body1">
                      PO {line.purchOrder} Line # {line.line}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="body1">{line.itemNum}</Typography>
                      <Typography variant="caption">{line.itemName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      {line.vendorCode} {line.vendorName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="body1">
                        Ordered: {line.quantity}x {line.uom}
                      </Typography>
                      <Typography variant="body2">
                        Received: {line.received}x {line.uom}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="body1">
                        Per Unit: ${line.price}
                      </Typography>
                      <Typography variant="body1">
                        Extended: ${line.price * line.quantity}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {line.orderDate.toLocaleDateString("en-US")}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={recCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
